// BULLETPROOF AppV3 - GUARANTEED TO NEVER BREAK
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef } from 'react';
import { BrowserRouter, Navigate, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QueryClient, QueryClientProvider, useQueryClient, type QueryFilters, type QueryKey } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { LanguageProvider } from './components/LanguageProvider';
import { ThemeProvider } from './components/ThemeProvider';
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SmartDashboardRoute } from './components/SmartDashboardRoute';
import { SmartKitchenRoute } from './components/SmartKitchenRoute';
import { SmartExpoRoute } from './components/SmartExpoRoute';
import { SmartOrdersRoute } from './components/SmartOrdersRoute';
import { SmartMenuRoute } from './components/SmartMenuRoute';
import { SmartRetailDashboardRoute } from './components/SmartRetailDashboardRoute';
import { readDemoSessionFromStorage } from './components/smart-routes/useSmartRouteResolution';
import ExpoPageV3 from '@/features/restaurant/pages/ExpoPage.V3';
import { BackgroundSyncProvider } from './components/BackgroundSyncProvider';
import { OfflineQueueInspector } from "./components/dev/OfflineQueueInspector";
import DashboardLayoutV3 from './components/DashboardLayout.V3';
import { tenantPath } from '@/lib/urls';
import { trackHelpDrawerEvent } from '@/lib/analytics/helpDrawer';
import { trackPageView } from '@/lib/analytics/ga';
import { useTenantPath } from '@/hooks/useTenantPath';
import { ProtectedRouteV2 } from '@/components/ProtectedRouteV2';
import OnboardingGuard from '@/components/OnboardingGuard';
import { useTenantSlug, useTenantStore } from '@/stores/tenantStore';
import { resetOfflineQueue } from '@/lib/offline/registerServiceWorker';
import { OfflineRuntimeProvider } from '@/components/OfflineRuntimeProvider';
import AuthDiagnostic from './pages/AuthDiagnostic';
import AiFeaturesPage from "./features/demo/classic/AiFeaturesPage";
import PricingPage from "./features/demo/classic/PricingPage";
import CostCalculatorPage from "./pages/marketing/CostCalculatorPage";
import StoriesPage from "./features/demo/classic/StoriesPage";
import AboutPage from "./features/demo/classic/AboutPage";
import TalkToBraisePage from "./features/demo/classic/TalkToBraisePage";
import BlogIndexPage from "./features/blog/BlogIndexPage";
import DocsPage from "./pages/DocsPage";
import NewOrderPeek from "./pages/sales/NewOrderPeek";
// Public Table Ordering page: eager import avoids flaky dynamic imports that broke SafeComponent fallbacks
import TableOrderPage from "./pages/public/TableOrder.V3";
import PayrollManagerDashboard from '@/features/payrollBridge/ManagerDashboard';
import PayrollOwnerDashboard from '@/features/payrollBridge/OwnerDashboard';
import PayrollEmployeePortalRoute from '@/features/payrollBridge/EmployeePortalRoute';
import { TipPoolingWorkspace } from '@/features/payrollBridge/pages/TipPoolingWorkspace';
import InventoryPage from '@/features/inventory/pages/InventoryPage.V3';
import RetailCheckoutPage from '@/features/retail/pages/RetailCheckoutPage.V3';
import FoodTruckModePage from '@/features/mobile/pages/FoodTruckModePage.V3';
import RushModePage from '@/features/mobile/pages/RushModePage.V3';
import AutopilotPage from '@/features/autopilot/pages/AutopilotPage.V3';
import AiStaffCopilotPage from '@/features/aiStaff/pages/AiStaffCopilotPage.V3';
import OnboardingPaymentPage from '@/pages/onboarding/Payment';
import OnboardingQuestionnairePage from '@/pages/onboarding/Questionnaire';
import { AuthProvider } from '@/hooks/useAuth';
import { OnboardingFlowProvider } from '@/features/onboarding/OnboardingFlowProvider';
import { SESSION_QUERY_KEY } from '@/lib/api/session';
import type { Query } from '@tanstack/react-query';

// Optimized Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const isSessionQuery = (queryKey: QueryKey): boolean => {
  if (!Array.isArray(queryKey)) {
    return false;
  }
  if (queryKey.length !== SESSION_QUERY_KEY.length) {
    return false;
  }
  return SESSION_QUERY_KEY.every((value, index) => queryKey[index] === value);
};

const TenantQueryBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const activeRestaurantId = useTenantStore((state) => state.activeRestaurantId);
  const tenantSlug = useTenantSlug();
  const cacheKey = `${activeRestaurantId ?? 'anonymous'}::${tenantSlug ?? 'default'}`;
  const previousKeyRef = useRef<string | null>(null);
  const nonSessionFiltersRef = useRef<QueryFilters>({
    predicate: (query: Query) => !isSessionQuery(query.queryKey),
  });
  const persistTimeoutRef = useRef<number | null>(null);

  const getStorage = () => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    try {
      return window.localStorage;
    } catch {
      return undefined;
    }
  };

  const storageKeyForTenant = useCallback((tenantKey: string) => {
    return `rq-cache-v1::${tenantKey}`;
  }, []);

  const shouldPersistQuery = (query: Query): boolean => {
    if (query.state.status !== 'success') {
      return false;
    }
    if (query.meta?.skipPersist === true) {
      return false;
    }
    return true;
  };

  const persistTenantQueries = useCallback(
    (tenantKey: string) => {
      const storage = getStorage();
      if (!storage) {
        return;
      }
      const queries = queryClient.getQueryCache().findAll(nonSessionFiltersRef.current);
      const entries = queries
        .filter(shouldPersistQuery)
        .map((query) => ({
          queryKey: query.queryKey,
          data: query.state.data,
          updatedAt: query.state.dataUpdatedAt ?? Date.now(),
        }));

      const payload = {
        version: 1,
        generatedAt: Date.now(),
        entries,
      };

      try {
        storage.setItem(storageKeyForTenant(tenantKey), JSON.stringify(payload));
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[TenantQueryBoundary] failed to persist queries', error);
        }
      }
    },
    [queryClient, storageKeyForTenant],
  );

  const schedulePersist = useCallback(
    (tenantKey: string) => {
      const storage = getStorage();
      if (!storage) {
        return;
      }
      if (persistTimeoutRef.current) {
        window.clearTimeout(persistTimeoutRef.current);
      }
      persistTimeoutRef.current = window.setTimeout(() => {
        persistTenantQueries(tenantKey);
        persistTimeoutRef.current = null;
      }, 750);
    },
    [persistTenantQueries],
  );

  const restoreTenantQueries = useCallback(
    (tenantKey: string) => {
      const storage = getStorage();
      if (!storage) {
        return;
      }
      const raw = storage.getItem(storageKeyForTenant(tenantKey));
      if (!raw) {
        return;
      }
      try {
        const parsed = JSON.parse(raw) as {
          entries?: Array<{ queryKey: QueryKey; data: unknown; updatedAt?: number }>;
        };
        if (!parsed?.entries?.length) {
          return;
        }
        for (const entry of parsed.entries) {
          if (!entry?.queryKey) {
            continue;
          }
          queryClient.setQueryData(entry.queryKey, entry.data, {
            updatedAt: typeof entry.updatedAt === 'number' ? entry.updatedAt : Date.now(),
          });
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[TenantQueryBoundary] failed to restore persisted queries', error);
        }
      }
    },
    [queryClient, storageKeyForTenant],
  );

  const resetTenantState = useCallback(async () => {
    try {
      const filters = nonSessionFiltersRef.current;
      await queryClient.cancelQueries(filters);
      queryClient.removeQueries(filters);
      await resetOfflineQueue();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[TenantQueryBoundary] failed to reset tenant state', error);
      }
    }
  }, [queryClient]);

  useEffect(() => {
    if (!cacheKey) {
      return;
    }

    let cancelled = false;

    const hydrateTenant = async () => {
      const previousKey = previousKeyRef.current;
      if (previousKey && previousKey !== cacheKey) {
        persistTenantQueries(previousKey);
        await resetTenantState();
      }
      if (!cancelled) {
        restoreTenantQueries(cacheKey);
        previousKeyRef.current = cacheKey;
      }
    };

    hydrateTenant().catch(() => {
      /* errors already logged in helpers */
    });

    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      schedulePersist(cacheKey);
    });

    return () => {
      cancelled = true;
      unsubscribe();
      schedulePersist(cacheKey);
    };
  }, [cacheKey, persistTenantQueries, queryClient, resetTenantState, restoreTenantQueries, schedulePersist]);

  return <>{children}</>;
};

// BULLETPROOF FALLBACK COMPONENTS
const HomeFallback = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-card text-card-foreground mx-auto flex w-full max-w-xl flex-col items-center rounded-xl border border-border p-10 text-center shadow-light">
      <h1 className="text-xl font-semibold tracking-tight">{t("fallback.homeTitle")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("fallback.homeLoading")}</p>
      <button
        type="button"
        className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-light transition hover:bg-primary/90"
        onClick={() => window.location.reload()}
      >
        {t("buttons.reload")}
      </button>
    </div>
  );
};
// Removed problematic DashboardFallback - using SmartDashboardRoute's own fallback

const PageFallback = ({ pageName }: { pageName: string }) => {
  const { t } = useTranslation();

  return (
    <div className="bg-card text-card-foreground mx-auto flex w-full max-w-xl flex-col items-center rounded-xl border border-border p-10 text-center shadow-light">
      <h1 className="text-xl font-semibold tracking-tight">{pageName}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("fallback.loading", { page: pageName })}</p>
      <button
        type="button"
        className="mt-6 inline-flex items-center justify-center rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground shadow-light transition hover:bg-secondary/80"
        onClick={() => window.history.back()}
      >
        {t("buttons.goBack")}
      </button>
    </div>
  );
};


const AppErrorFallback = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-destructive/10 text-destructive mx-auto flex w-full max-w-md flex-col items-center gap-3 rounded-xl border border-destructive/20 p-6 text-center shadow-light">
      <p className="text-base font-semibold">{t("fallback.errorTitle")}</p>
      <p className="text-sm text-muted-foreground">{t("fallback.errorDescription")}</p>
      <button
        type="button"
        className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-light transition hover:bg-primary/90"
        onClick={() => window.location.reload()}
      >
        {t("buttons.reload")}
      </button>
    </div>
  );
};
// SAFE COMPONENT WRAPPER
type SafeComponentProps = {
  children: React.ReactNode;
  fallback: React.ReactNode;
  name: string;
  resetKeys?: unknown[];
};

const SafeComponent = ({ children, fallback, name, resetKeys }: SafeComponentProps) => {
  const locationKey =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : '';

  const derivedResetKeys = useMemo(() => {
    if (Array.isArray(resetKeys) && resetKeys.length > 0) {
      return resetKeys;
    }
    return [name, locationKey];
  }, [resetKeys, name, locationKey]);

  return (
    <ErrorBoundary fallback={fallback} componentName={name} resetKeys={derivedResetKeys}>
      <Suspense fallback={<div className="text-sm text-muted-foreground">Loading {name}...</div>}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

const DemoAwareShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    const path = `${location.pathname}${location.search}${location.hash ?? ''}`;
    trackPageView(path);
  }, [location.pathname, location.search, location.hash]);

  const shouldEnableDemo = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('mode') === 'demo') {
      return true;
    }

    try {
      const slugMatch = location.pathname.match(/^\/r\/([^/]+)/i);
      const expectedSlug = slugMatch ? slugMatch[1] : null;
      return readDemoSessionFromStorage(expectedSlug);
    } catch {
      return false;
    }
  }, [location.pathname, location.search]);

  if (!shouldEnableDemo) {
    return <>{children}</>;
  }

  return (
    <DemoProvider>
      <BackgroundSyncProvider>
        <SafeComponent fallback={null} name="DemoBadge">
          <DemoBadge />
        </SafeComponent>
        <SafeComponent fallback={null} name="DemoDebugger">
          <DemoDebugger />
        </SafeComponent>
        {children}
      </BackgroundSyncProvider>
    </DemoProvider>
  );
};

const LegacyRedirect = ({ page, renderFallback }: { page: string; renderFallback: () => React.ReactNode }) => {
  const slug = useTenantSlug();
  if (!slug) {
    return renderFallback();
  }
  return <Navigate to={tenantPath(slug, page)} replace />;
};
const LegacyReportsRedirect = () => {
  const slug = useTenantSlug();
  if (slug) {
    return <Navigate to={tenantPath(slug, 'reports')} replace />;
  }
  return (
    <SafeComponent fallback={<PageFallback pageName="Reports" />} name="ReportsLegacy">
      <Reports />
    </SafeComponent>
  );
};

const TenantRedirect = ({ to }: { to: string }) => {
  const { tenantPath, slug } = useTenantPath();
  if (!slug) {
    return <Navigate to="/auth/login" replace />;
  }
  return <Navigate to={tenantPath(to)} replace />;
};


const DashboardLayoutRoute = () => (
  <ThemeProvider defaultTheme="system" storageKey="braise-theme">
    <OnboardingGuard>
      <DashboardLayoutV3>
        <Outlet />
      </DashboardLayoutV3>
    </OnboardingGuard>
  </ThemeProvider>
);

// LAZY IMPORTS WITH ERROR HANDLING
const ClassicIndexPage = lazy(() =>
  import("./features/demo/classic/ClassicIndexPage")
    .catch(() => ({ default: HomeFallback }))
);

// Removed ClassicDemoDashboard - using SmartDashboardRoute instead

const DemoProvider = lazy(() =>
  import("./features/demo/components/DemoProvider.V3")
    .then(m => ({ default: m.DemoProviderV3 ?? m.DemoProvider }))
    .catch(() => ({ default: ({ children }: { children: React.ReactNode }) => <>{children}</> }))
);

const DemoBadge = lazy(() =>
  import("./features/demo/components/DemoBadge.V3")
    .then(m => ({ default: m.DemoBadgeV3 ?? m.DemoBadge }))
    .catch(() => ({ default: () => null }))
);

const DemoDebugger = lazy(() =>
  import("./features/demo/components/DemoDebugger.V3")
    .then(m => ({ default: m.DemoDebuggerV3 ?? m.DemoDebugger }))
    .catch(() => ({ default: () => null }))
);

// Original UI demo pages with fallbacks
const IntegrationStorePage = lazy(() =>
  import("./features/integrations/pages/IntegrationStorePage")
    .catch(() => ({ default: () => <PageFallback pageName="Integration Store" /> }))
);

const IntegrationsPage = lazy(() =>
  import("./features/integrations/pages/IntegrationsPage")
    .catch(() => ({ default: () => <PageFallback pageName="Integrations" /> }))
);

const StaffPage = lazy(() =>
  import("./features/restaurant/pages/StaffPage.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Staff" /> }))
);

const SettingsPage = lazy(() =>
  import("./features/restaurant/pages/SettingsPage.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Settings" /> }))
);

const TrainingPage = lazy(() =>
  import("./features/restaurant/pages/TrainingPage.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Training" /> }))
);

const KioskOrderPage = lazy(() =>
  import("./pages/kiosk/KioskOrder.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Kiosk" /> }))
);

// New Order (Counter) page
const NewOrderPage = lazy(() =>
  import("./pages/sales/NewOrder.V3")
    .catch(() => ({ default: () => <PageFallback pageName="New Order" /> }))
);

const OrderSuccessPage = lazy(() =>
  import("./pages/pos/OrderSuccessPage")
    .catch(() => ({ default: () => <PageFallback pageName="Order Success" /> }))
);

const OfflineReconciliationPage = lazy(() =>
  import("./pages/pos/OfflineReconciliationPage")
    .catch(() => ({ default: () => <PageFallback pageName="Offline Reconciliation" /> }))
);

// Wrapper to log when DemoProvider is mounted for NewOrder in demo mode
const NewOrder = () => {
  try {
    const mode = new URLSearchParams(window.location.search).get('mode');
    if (mode === 'demo' && import.meta.env?.DEV) {
      console.debug('[NewOrderRoute] DemoProvider mounted for NewOrder');
    }
  } catch (error) {
    console.warn('[NewOrderRoute] failed to inspect query params', error);
  }
  return <NewOrderPage />;
};

const OrderSuccess = () => (
  <ProtectedRouteV2 legacyPage="orders">
    <OrderSuccessPage />
  </ProtectedRouteV2>
);

const OfflineReconciliation = () => (
  <ProtectedRouteV2 legacyPage="pos">
    <OfflineReconciliationPage />
  </ProtectedRouteV2>
);

// Auth components (use V2 screens for public flows)
const Login = lazy(() =>
  import("./pages/auth/Login")
    .catch(() => ({ default: () => <PageFallback pageName="Login" /> }))
);

const Signup = lazy(() =>
  import("./pages/auth/Signup")
    .catch(() => ({ default: () => <PageFallback pageName="Signup" /> }))
);

const AuthCallback = lazy(() =>
  import("./pages/auth/Callback")
    .catch(() => ({ default: () => <PageFallback pageName="Auth Callback" /> }))
);

// Real restaurant components  
const RealDashboardPage = lazy(() =>
  import("./features/restaurant/pages/RealDashboardPage.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Dashboard" /> }))
);

const Kitchen = lazy(() =>
  import("./pages/Kitchen.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Kitchen" /> }))
);

const Orders = lazy(() =>
  import("./pages/Orders.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Orders" /> }))
);

const Menu = lazy(() =>
  import("./pages/Menu.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Menu" /> }))
);

const Tables = lazy(() =>
  import("./pages/Tables.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Tables" /> }))
);

const Inventory = lazy(() =>
  import("./pages/Inventory.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Inventory" /> }))
);

const Customers = lazy(() =>
  import("./pages/Customers.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Customers" /> }))
);

const CateringEvents = lazy(() =>
  import("./pages/CateringEvents.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Catering & Events" /> }))
);

const CateringEventDetail = lazy(() =>
  import("./pages/CateringEventDetail.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Catering & Events" /> }))
);

const QRCodes = lazy(() =>
  import("./pages/QRCodes.V3")
    .catch(() => ({ default: () => <PageFallback pageName="QR Codes" /> }))
);

const Payments = lazy(() =>
  import("./pages/Payments.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Payments" /> }))
);

const Reports = lazy(() =>
  import("./pages/Reports.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Reports" /> }))
);

const RewardRedemptions = lazy(() =>
  import("./pages/RewardRedemptions.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Reward Redemptions" /> }))
);

const StaffSettings = lazy(() =>
  import("./pages/settings/Staff.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Staff" /> }))
);

const PrintersSettings = lazy(() =>
  import("./pages/settings/Printers.V3")
    .catch(() => ({ default: () => <PageFallback pageName="Printers" /> }))
);

const HandheldPage = lazy(() =>
  import("./pages/handheld/HandheldPage")
    .catch(() => ({ default: () => <PageFallback pageName="Handheld" /> }))
);

const AppV3: React.FC = () => {
  if (import.meta.env.DEV) {
    console.debug('[AppV3] starting render');
  }

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    const mediaQuery = window.matchMedia('(pointer: fine)');
    const applyPointerPreference = () => {
      if (mediaQuery.matches) {
        root.setAttribute('data-pointer', 'fine');
      } else {
        root.removeAttribute('data-pointer');
      }
    };
    applyPointerPreference();
    mediaQuery.addEventListener('change', applyPointerPreference);
    return () => {
      mediaQuery.removeEventListener('change', applyPointerPreference);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{
        id?: string;
        event?: 'open' | 'close';
        openedAt?: number | null;
        closedAt?: number | null;
      }>;

      const detail = customEvent.detail;
      if (!detail || typeof detail.id !== 'string') {
        return;
      }

      const action = detail.event;
      if (action !== 'open' && action !== 'close') {
        return;
      }

      const openedAt =
        typeof detail.openedAt === 'number' && Number.isFinite(detail.openedAt)
          ? detail.openedAt
          : null;
      const closedAt =
        typeof detail.closedAt === 'number' && Number.isFinite(detail.closedAt)
          ? detail.closedAt
          : null;

      trackHelpDrawerEvent({
        drawerId: detail.id,
        action,
        openedAt: openedAt ? new Date(openedAt).toISOString() : null,
        closedAt: closedAt ? new Date(closedAt).toISOString() : null,
        durationMs:
          openedAt !== null && closedAt !== null ? Math.max(0, closedAt - openedAt) : null,
      });
    };

    window.addEventListener('braise:help-drawer', handler as EventListener);
    return () => {
      window.removeEventListener('braise:help-drawer', handler as EventListener);
    };
  }, []);

  return (
    <ErrorBoundary fallback={<AppErrorFallback />}>
      <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <TenantQueryBoundary>
            <OfflineRuntimeProvider>
              <AuthProvider>
                <OnboardingFlowProvider>
                  <TooltipProvider>
                    <Toaster />
                    <Sonner />

                    <SafeComponent
                      fallback={<div>{/* Demo provider failed, but app continues */}</div>}
                      name="DemoProvider"
                    >
                      <BrowserRouter
                        future={{
                          v7_startTransition: true,
                          v7_relativeSplatPath: true,
                        }}
                      >
                        <DemoAwareShell>
                          <OfflineQueueInspector />
                          <Routes>
                            {/* Home route */}
                            <Route
                              path="/"
                              element={
                                <SafeComponent
                                  fallback={<HomeFallback />}
                                  name="HomePage"
                                >
                                  <ClassicIndexPage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/ai-features"
                              element={
                                <SafeComponent
                                  fallback={<HomeFallback />}
                                  name="AiFeaturesPage"
                                >
                                  <AiFeaturesPage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/blog"
                              element={
                                <SafeComponent
                                  fallback={<HomeFallback />}
                                  name="BlogIndexPage"
                                >
                                  <BlogIndexPage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/pricing"
                              element={
                                <SafeComponent
                                  fallback={<HomeFallback />}
                                  name="PricingPage"
                                >
                                  <PricingPage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/cost-calculator"
                              element={
                                <SafeComponent
                                  fallback={<HomeFallback />}
                                  name="CostCalculatorPage"
                                >
                                  <CostCalculatorPage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/stories"
                              element={
                                <SafeComponent
                                  fallback={<HomeFallback />}
                                  name="StoriesPage"
                                >
                                  <StoriesPage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/about"
                              element={
                                <SafeComponent
                                  fallback={<HomeFallback />}
                                  name="AboutPage"
                                >
                                  <AboutPage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/docs"
                              element={
                                <SafeComponent
                                  fallback={<HomeFallback />}
                                  name="DocsPage"
                                >
                                  <DocsPage />
                                </SafeComponent>
                              }
                            />

                            {/* Auth routes */}
                            <Route
                              path="/auth/login"
                              element={
                                <SafeComponent
                                  fallback={<PageFallback pageName="Login" />}
                                  name="Login"
                                >
                                  <Login />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/auth/signup"
                              element={
                                <SafeComponent
                                  fallback={<PageFallback pageName="Signup" />}
                                  name="Signup"
                                >
                                  <Signup />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/auth/callback"
                              element={
                                <SafeComponent
                                  fallback={<PageFallback pageName="Callback" />}
                                  name="AuthCallback"
                                >
                                  <AuthCallback />
                                </SafeComponent>
                              }
                            />

                            <Route
                              path="/onboarding/payment"
                              element={
                                <SafeComponent
                                  fallback={<PageFallback pageName="Onboarding Payment" />}
                                  name="OnboardingPayment"
                                >
                                  <OnboardingPaymentPage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="/onboarding/questionnaire"
                              element={
                                <SafeComponent
                                  fallback={<PageFallback pageName="Onboarding Questionnaire" />}
                                  name="OnboardingQuestionnaire"
                                >
                                  <OnboardingQuestionnairePage />
                                </SafeComponent>
                              }
                            />

                            {/* Public Table Ordering Route */}
                            <Route
                              path="/r/:subdomain/table/:qrSlug"
                              element={
                                <SafeComponent
                                  fallback={<PageFallback pageName="Table Order" />}
                                  name="TableOrder"
                                >
                                  <TableOrderPage />
                                </SafeComponent>
                              }
                            />
                            {/* Public Kiosk Route */}
                            <Route
                              path="/r/:subdomain/kiosk"
                              element={
                                <SafeComponent
                                  fallback={<PageFallback pageName="Kiosk" />}
                                  name="KioskOrder"
                                >
                                  <KioskOrderPage />
                                </SafeComponent>
                              }
                            />

                            <Route element={<DashboardLayoutRoute />}>
                              {/* Smart Dashboard Route (Demo or Real) */}
                              <Route
                                path="/r/:subdomain/dashboard"
                                element={<SmartDashboardRoute />}
                              />
                              <Route
                                path="/r/:subdomain/retail"
                                element={<SmartRetailDashboardRoute />}
                              />
                              <Route
                                path="/r/:subdomain/retail/inventory"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Retail Inventory" />}
                                    name="RetailInventory"
                                  >
                                    <InventoryPage />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/retail/checkout"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Retail Checkout" />}
                                    name="RetailCheckout"
                                  >
                                    <RetailCheckoutPage />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/mobile/food-truck"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Food Truck Mode" />}
                                    name="FoodTruckMode"
                                  >
                                    <FoodTruckModePage />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/ops/rush"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Rush Mode" />}
                                    name="RushMode"
                                  >
                                    <RushModePage />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/catering/events"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Catering & Events" />}
                                    name="CateringEvents"
                                  >
                                    <CateringEvents />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/catering/events/:eventId"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Catering & Events" />}
                                    name="CateringEventDetail"
                                  >
                                    <CateringEventDetail />
                                  </SafeComponent>
                                }
                              />

                              {/* Smart Routes (Demo or Real) */}
                              <Route
                                path="/r/:subdomain/kitchen"
                                element={<SmartKitchenRoute />}
                              />

                              <Route
                                path="/r/:subdomain/expo"
                                element={<SmartExpoRoute />}
                              />

                              <Route
                                path="/r/:subdomain/orders"
                                element={<SmartOrdersRoute />}
                              />

                              <Route
                                path="/r/:subdomain/menu"
                                element={<SmartMenuRoute />}
                              />

                              <Route
                                path="/r/:subdomain/integration-store"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Integration Store" />}
                                    name="IntegrationStore"
                                  >
                                    <IntegrationStorePage />
                                  </SafeComponent>
                                }
                              />

                              <Route
                                path="/r/:subdomain/integrations"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Integrations" />}
                                    name="Integrations"
                                  >
                                    <IntegrationsPage />
                                  </SafeComponent>
                                }
                              />

                              <Route
                                path="/r/:subdomain/qr-codes"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="QR Codes" />}
                                    name="QRCodes"
                                  >
                                    <QRCodes />
                                  </SafeComponent>
                                }
                              />

                              <Route
                                path="/r/:subdomain/customers"
                                element={
                                  <ProtectedRouteV2 requiredPermission="customers.view">
                                    <SafeComponent
                                      fallback={<PageFallback pageName="Customers" />}
                                      name="Customers"
                                    >
                                      <Customers />
                                    </SafeComponent>
                                  </ProtectedRouteV2>
                                }
                              />

                              <Route
                                path="/r/:subdomain/settings"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Settings" />}
                                    name="Settings"
                                  >
                                    <ProtectedRouteV2 legacyPage="settings">
                                      <SettingsPage />
                                    </ProtectedRouteV2>
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/settings/staff"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Staff" />}
                                    name="StaffSettings"
                                  >
                                    <StaffSettings />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/settings/printers"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Printers" />}
                                    name="PrintersSettings"
                                  >
                                    <PrintersSettings />
                                  </SafeComponent>
                                }
                              />

                              <Route
                                path="/r/:subdomain/payroll/owner"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Payroll Owner Dashboard" />}
                                    name="PayrollOwner"
                                  >
                                    <ProtectedRouteV2 requiredPermission="payroll.bridge.manage" allowDemo>
                                      <PayrollOwnerDashboard />
                                    </ProtectedRouteV2>
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/payroll/onboarding"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Payroll Onboarding" />}
                                    name="PayrollOnboarding"
                                  >
                                    <ProtectedRouteV2 requiredPermission="payroll.bridge.manage" allowDemo>
                                      <PayrollManagerDashboard />
                                    </ProtectedRouteV2>
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/payroll/tips"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Tip Pooling" />}
                                    name="PayrollTipPooling"
                                  >
                                    <ProtectedRouteV2 requiredPermission="payroll.bridge.manage" allowDemo>
                                      <TipPoolingWorkspace />
                                    </ProtectedRouteV2>
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/payroll/ess/:employeeId"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Employee Self-Service" />}
                                    name="PayrollEss"
                                  >
                                    <ProtectedRouteV2 requiredPermission="payroll.bridge.ess" allowDemo>
                                      <PayrollEmployeePortalRoute />
                                    </ProtectedRouteV2>
                                  </SafeComponent>
                                }
                              />

                              <Route
                                path="/r/:subdomain/staff"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Staff" />}
                                    name="Staff"
                                  >
                                    <StaffPage />
                                  </SafeComponent>
                                }
                              />

                              <Route
                                path="/r/:subdomain/training"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Training" />}
                                    name="Training"
                                  >
                                    <TrainingPage />
                                  </SafeComponent>
                                }
                              />

                              <Route
                                path="/r/:subdomain/payments"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Payments" />}
                                    name="Payments"
                                  >
                                    <Payments />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/reports"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Reports" />}
                                    name="Reports"
                                  >
                                    <Reports />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/reports/rewards"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Reward Redemptions" />}
                                    name="RewardRedemptions"
                                  >
                                    <RewardRedemptions />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/autopilot"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Autopilot" />}
                                    name="AutopilotPrototypeTenant"
                                  >
                                    <AutopilotPage />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/ai-staff"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="AI Staff Copilot" />}
                                    name="AiStaffCopilotTenant"
                                  >
                                    <AiStaffCopilotPage />
                                  </SafeComponent>
                                }
                              />

                              {/* Auth Diagnostic Route */}
                              <Route
                                path="/auth-diagnostic"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Auth Diagnostic" />}
                                    name="AuthDiagnostic"
                                  >
                                    <AuthDiagnostic />
                                  </SafeComponent>
                                }
                              />

                              {/* Real restaurant routes (authenticated) */}
                              <Route
                                path="/dashboard"
                                element={
                                  <LegacyRedirect
                                    page="dashboard"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Dashboard" />}
                                        name="RealDashboard"
                                      >
                                        <RealDashboardPage />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/kitchen"
                                element={
                                  <LegacyRedirect
                                    page="kitchen"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Kitchen" />}
                                        name="RealKitchen"
                                      >
                                        <Kitchen />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/expo"
                                element={
                                  <LegacyRedirect
                                    page="expo"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Expo" />}
                                        name="ExpoPage"
                                      >
                                        <ExpoPageV3 />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/orders"
                                element={
                                  <LegacyRedirect
                                    page="orders"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Orders" />}
                                        name="RealOrders"
                                      >
                                        <Orders />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/menu"
                                element={
                                  <LegacyRedirect
                                    page="menu"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Menu" />}
                                        name="RealMenu"
                                      >
                                        <Menu />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/integration-store"
                                element={
                                  <LegacyRedirect
                                    page="integration-store"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Integration Store" />}
                                        name="IntegrationStoreLegacy"
                                      >
                                        <IntegrationStorePage />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/integrations"
                                element={
                                  <LegacyRedirect
                                    page="integrations"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Integrations" />}
                                        name="IntegrationsLegacy"
                                      >
                                        <IntegrationsPage />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/tables"
                                element={
                                  <LegacyRedirect
                                    page="tables"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Tables" />}
                                        name="Tables"
                                      >
                                        <Tables />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/inventory"
                                element={
                                  <LegacyRedirect
                                    page="inventory"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Inventory" />}
                                        name="Inventory"
                                      >
                                        <Inventory />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/customers"
                                element={
                                  <LegacyRedirect
                                    page="customers"
                                    renderFallback={() => (
                                      <ProtectedRouteV2 requiredPermission="customers.view">
                                        <SafeComponent
                                          fallback={<PageFallback pageName="Customers" />}
                                          name="Customers"
                                        >
                                          <Customers />
                                        </SafeComponent>
                                      </ProtectedRouteV2>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/retail"
                                element={
                                  <LegacyRedirect
                                    page="retail"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Retail" />}
                                        name="Retail"
                                      >
                                        <SmartRetailDashboardRoute />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/retail/inventory"
                                element={
                                  <LegacyRedirect
                                    page="retail/inventory"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Retail Inventory" />}
                                        name="RetailInventoryLegacy"
                                      >
                                        <InventoryPage />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/retail/checkout"
                                element={
                                  <LegacyRedirect
                                    page="retail/checkout"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Retail Checkout" />}
                                        name="RetailCheckoutLegacy"
                                      >
                                        <RetailCheckoutPage />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/catering/events"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Catering & Events" />}
                                    name="CateringEvents"
                                  >
                                    <CateringEvents />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/catering/events/:eventId"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Catering & Events" />}
                                    name="CateringEventDetail"
                                  >
                                    <CateringEventDetail />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/qr-codes"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="QR Codes" />}
                                    name="RealQRCodes"
                                  >
                                    <QRCodes />
                                  </SafeComponent>
                                }
                              />

                              <Route
                                path="/payments"
                                element={
                                  <LegacyRedirect
                                    page="payments"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Payments" />}
                                        name="PaymentsLegacy"
                                      >
                                        <Payments />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/reports"
                                element={<LegacyReportsRedirect />}
                              />
                              <Route
                                path="/reports/rewards"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Reward Redemptions" />}
                                    name="RewardRedemptionsPublic"
                                  >
                                    <RewardRedemptions />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/autopilot"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Autopilot" />}
                                    name="AutopilotPrototype"
                                  >
                                    <AutopilotPage />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/ai-staff"
                                element={
                                  <LegacyRedirect
                                    page="ai-staff"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="AI Staff Copilot" />}
                                        name="AiStaffCopilot"
                                      >
                                        <AiStaffCopilotPage />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/training"
                                element={
                                  <LegacyRedirect
                                    page="training"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Training" />}
                                        name="TrainingLegacy"
                                      >
                                        <TrainingPage />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />


                              <Route
                                path="/settings/staff"
                                element={
                                  <LegacyRedirect
                                    page="settings/staff"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Staff" />}
                                        name="StaffSettingsLegacy"
                                      >
                                        <StaffSettings />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />
                              <Route
                                path="/settings/printers"
                                element={
                                  <LegacyRedirect
                                    page="settings/printers"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Printers" />}
                                        name="PrintersSettingsLegacy"
                                      >
                                        <PrintersSettings />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />

                              <Route
                                path="/staff"
                                element={
                                  <LegacyRedirect
                                    page="settings/staff"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Staff" />}
                                        name="StaffLegacy"
                                      >
                                        <StaffSettings />
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />

                              <Route
                                path="/settings"
                                element={
                                  <LegacyRedirect
                                    page="settings"
                                    renderFallback={() => (
                                      <SafeComponent
                                        fallback={<PageFallback pageName="Settings" />}
                                        name="SettingsLegacy"
                                      >
                                        <ProtectedRouteV2 legacyPage="settings">
                                          <SettingsPage />
                                        </ProtectedRouteV2>
                                      </SafeComponent>
                                    )}
                                  />
                                }
                              />

                              {/* New Counter Order */}
                              <Route
                                path="/r/:subdomain/sales/new/peek"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="New Order" />}
                                    name="NewOrderPeek"
                                  >
                                    <NewOrderPeek />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/sales/new/peek"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="New Order" />}
                                    name="NewOrderPeekTenantless"
                                  >
                                    <NewOrderPeek />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/sales/new"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="New Order" />}
                                    name="NewOrder"
                                  >
                                    <NewOrder />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/sales/new"
                                element={<TenantRedirect to="sales/new" />}
                              />
                              <Route
                                path="/r/:subdomain/orders/new"
                                element={<Navigate to="../sales/new" replace />}
                              />
                              <Route
                                path="/pos/new"
                                element={<TenantRedirect to="sales/new" />}
                              />

                              <Route
                                path="/r/:subdomain/pos/orders/:orderId/success"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Order Success" />}
                                    name="OrderSuccess"
                                  >
                                    <OrderSuccess />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/pos/orders/:orderId/success"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Order Success" />}
                                    name="OrderSuccessTenantless"
                                  >
                                    <OrderSuccess />
                                  </SafeComponent>
                                }
                              />

                              <Route
                                path="/r/:subdomain/pos/offline/reconciliation"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Offline Reconciliation" />}
                                    name="OfflineReconciliation"
                                  >
                                    <OfflineReconciliation />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/pos/offline/reconciliation"
                                element={<TenantRedirect to="pos/offline/reconciliation" />}
                              />

                              <Route
                                path="/handheld"
                                element={
                                  <SafeComponent
                                    fallback={<PageFallback pageName="Handheld Order Pad" />}
                                    name="HandheldOrderPad"
                                  >
                                    <HandheldPage />
                                  </SafeComponent>
                                }
                              />
                              <Route
                                path="/r/:subdomain/handheld"
                                element={<TenantRedirect to="handheld" />}
                              />

                            </Route>

                            {/* 404 fallback */}
                            <Route
                              path="/talk-to-braise"
                              element={
                                <SafeComponent fallback={<HomeFallback />} name="TalkToBraisePage">
                                  <TalkToBraisePage />
                                </SafeComponent>
                              }
                            />
                            <Route
                              path="*"
                              element={
                                <div className="bg-card text-card-foreground mx-auto flex w-full max-w-md flex-col items-center rounded-xl border border-border p-10 text-center shadow-light">
                                  <h2 className="text-lg font-semibold">404 - Page Not Found</h2>
                                  <a
                                    className="mt-3 text-sm font-medium text-primary underline-offset-4 hover:underline"
                                    href="/"
                                  >
                                    Go back to home
                                  </a>
                                </div>
                              }
                            />
                          </Routes>
                        </DemoAwareShell>
                      </BrowserRouter>
                    </SafeComponent>
                  </TooltipProvider>
                </OnboardingFlowProvider>
              </AuthProvider>
            </OfflineRuntimeProvider>
          </TenantQueryBoundary>
        </QueryClientProvider>
      </LanguageProvider>
    </ErrorBoundary>
  )
}

export default AppV3
